import React, { useRef, useEffect, FunctionComponent } from 'react';
import { useReactToPrint } from 'react-to-print';
import { CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { formatMoney } from 'accounting';
import { language } from '../../utils/language';
import { getReferenceDisplayText } from '../../utils/referencePresentation';

type Props = {
    purchase: any;
    transactionState: { transactionDate: string; transactionTime: string };
    homeState: HomeStateType;
    payment: number;
    items: any[];
    status: 'success' | 'pending' | 'error';
    contactEcommerce: { email: string; phone: string };
    onClose: () => void;
    isTestTransaction?: boolean;
};

const DEFAULT_BRAND = '#F5821F';

const STATUS_ICON: Record<string, JSX.Element> = {
    success: <CheckCircle size={40} color="#4CAF50" weight="regular" />,
    pending: <WarningCircle size={40} color="#F5A623" weight="regular" />,
    error: <XCircle size={40} color="#E53935" weight="regular" />,
};

const STATUS_TEXT: Record<string, { titleKey: string; messageKey: string }> = {
    success: { titleKey: 'paymentCompleted', messageKey: 'paymentConfirmedMsg' },
    test: { titleKey: 'paymentTestModeCompleted', messageKey: 'paymentTestModeConfirmedMsg' },
    pending: { titleKey: 'pendingPay', messageKey: 'messagePendingPay' },
    error: { titleKey: 'rejectPay', messageKey: 'messageRejectPay' },
};

const PrintContent = React.forwardRef<HTMLDivElement, Props>(
    ({ purchase, transactionState, homeState, payment, items, status, contactEcommerce, isTestTransaction }, ref) => {
        const lang = language[homeState.default_language];
        const accentColor = homeState.color || DEFAULT_BRAND;

        // Modo prueba de esta transaccion (invoice.test), lo resuelve el contenedor
        const isTestMode = !!isTestTransaction;
        const statusText = status === 'success' && isTestMode ? 'test' : status;

        const formatAmount = (amount: number): string => {
            const cur = homeState.currency || 'COP';
            if (cur === 'USD') return `${formatMoney(amount, { symbol: '$', thousand: ',', decimal: '.', precision: 0 })} USD`;
            return `${formatMoney(amount, { symbol: '$', thousand: '.', decimal: ',', precision: 0 })} COP`;
        };

        const carrierName =
            purchase.carrier?.nameCarrier ??
            purchase.carrier?.name ??
            purchase.shipping?.nameCarrier ??
            purchase.shipping?.operador ??
            '';

        const getEstimatedDate = (): string => {
            const raw = purchase.carrier?.daysDelivery ?? purchase.shipping?.daysDelivery;
            if (!raw) {
                const base = purchase.date ? new Date(purchase.date) : new Date();
                let added = 0;
                while (added < 3) { base.setDate(base.getDate() + 1); if (base.getDay() !== 0 && base.getDay() !== 6) added++; }
                return `${String(base.getDate()).padStart(2, '0')}/${String(base.getMonth() + 1).padStart(2, '0')}/${base.getFullYear()}`;
            }
            if (typeof raw === 'string') {
                const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw.trim());
                if (match) return raw.trim();
                const d = new Date(raw);
                if (!isNaN(d.getTime())) return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
            }
            return '';
        };

        const orderNumber = purchase.guideId ?? purchase.guide?.aveonline?.data?.guia ?? null;

        const getPurchaseDate = (): { date: string; time: string } => {
            if (!purchase.date) return { date: '', time: '' };
            const d = new Date(purchase.date);
            if (isNaN(d.getTime())) return { date: '', time: '' };
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const h = d.getHours();
            const m = d.getMinutes();
            const period = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            return {
                date: `${dd}/${mm}/${d.getFullYear()}`,
                time: `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`,
            };
        };

        const dateFallback = getPurchaseDate();

        const getProductImage = (product: any): string => {
            const referenceImg = product?.references?.[0]?.data?.img;
            if (product.references?.length > 0 && typeof referenceImg === 'string' && referenceImg !== '' && !referenceImg.endsWith('/null')) {
                return referenceImg;
            }
            return product.productData.img?.[0] ?? '';
        };

        const estimatedDate = getEstimatedDate();

        const s: Record<string, React.CSSProperties> = {
            page: {
                fontFamily: "'Open Sans', sans-serif",
                color: '#222',
                maxWidth: 600,
                margin: '0 auto',
                padding: '20px 28px',
                fontSize: 12,
            },
            topBar: {
                height: 4,
                background: accentColor,
                borderRadius: 2,
                marginBottom: 14,
            },
            dateRow: {
                fontSize: 9,
                color: '#999',
                textAlign: 'right' as const,
                marginBottom: 6,
            },
            header: {
                textAlign: 'center' as const,
                marginBottom: 14,
                padding: '12px 0 10px',
            },
            title: {
                fontFamily: "'Raleway', sans-serif",
                fontSize: 19,
                fontWeight: 700,
                margin: '6px 0 2px',
                color: '#222',
            },
            subtitle: {
                fontSize: 11,
                color: '#666',
                margin: '0 auto',
                maxWidth: 400,
                lineHeight: 1.4,
            },
            card: {
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 8,
            },
            cardRow: {
                display: 'flex',
                justifyContent: 'space-between' as const,
                gap: 14,
            },
            label: {
                fontSize: 10,
                fontWeight: 700,
                color: '#555',
                marginBottom: 1,
            },
            labelDark: {
                fontSize: 10,
                fontWeight: 700,
                color: '#333',
                marginBottom: 1,
            },
            value: {
                fontSize: 11,
                color: '#444',
                lineHeight: 1.4,
            },
            amount: {
                fontSize: 16,
                fontWeight: 700,
                color: accentColor,
            },
            sectionTitle: {
                fontFamily: "'Raleway', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: '#333',
                margin: '12px 0 5px',
            },
            grid3: {
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
            },
            grid2: {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                marginTop: 6,
            },
            productsBox: {
                background: '#FAFAFA',
                borderRadius: 8,
                border: '1px solid #EFEFEF',
                padding: '2px 0',
                marginBottom: 8,
            },
            productRow: {
                display: 'flex',
                alignItems: 'center' as const,
                gap: 8,
                padding: '5px 12px',
                borderBottom: '1px solid #F5F5F5',
            },
            productName: {
                flex: '1.5',
                fontWeight: 700,
                fontSize: 11,
                color: '#222',
            },
            productMeta: {
                flex: '1',
                fontSize: 10,
                color: '#777',
            },
            productImg: {
                width: 32,
                height: 32,
                objectFit: 'cover' as const,
                borderRadius: 4,
                flexShrink: 0,
                border: '1px solid #eee',
            },
            footer: {
                borderTop: '1px solid #e0e0e0',
                borderRadius: 0,
                padding: '10px 12px 0',
                textAlign: 'center' as const,
                marginTop: 12,
            },
            footerTitle: {
                fontSize: 10,
                fontWeight: 700,
                color: '#555',
                marginBottom: 2,
            },
            footerText: {
                fontSize: 10,
                color: '#555',
            },
        };

        return (
            <div ref={ref} style={s.page}>
                <div style={s.topBar} />

                <div style={s.dateRow}>
                    {dateFallback.date} {dateFallback.time}
                </div>

                <div style={s.header}>
                    {STATUS_ICON[status]}
                    <div style={s.title}>{lang[STATUS_TEXT[statusText].titleKey]}</div>
                    <p style={s.subtitle}>{lang[STATUS_TEXT[statusText].messageKey]}</p>
                </div>

                {status === 'success' && (
                    <div style={s.card}>
                        <div style={s.cardRow}>
                            <div style={{ flex: 1 }}>
                                <div style={s.label}>{lang['orderNumber']}</div>
                                <div style={{ ...s.value, marginBottom: 4, fontWeight: 600 }}>
                                    {orderNumber ?? (isTestMode ? lang['aveGeneratingGuideTest'] : lang['aveGeneratingGuide'])}
                                </div>
                                <div style={{ fontSize: 10, color: '#555', maxWidth: 220, lineHeight: 1.4 }}>
                                    {lang['emailConfirmationMsg']} <strong>{purchase.contactEmail || purchase.email}</strong>
                                    {lang['emailConfirmationMsg2']}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' as const }}>
                                <div style={s.label}>{lang['valuePaid']}</div>
                                <div style={s.amount}>{formatAmount(Math.abs(payment))}</div>
                                <div style={{ ...s.labelDark, marginTop: 6 }}>{lang['orderDetail']}</div>
                                <div style={s.value}>{dateFallback.date}</div>
                                <div style={s.value}>{dateFallback.time}</div>
                            </div>
                        </div>
                    </div>
                )}

                {status !== 'error' && (
                    <>
                        <div style={s.sectionTitle}>{lang['shippingDetailTitle']}</div>
                        <div style={s.card}>
                            <div style={s.grid3}>
                                <div>
                                    <div style={s.label}>{lang['customerData']}</div>
                                    <div style={s.value}>{purchase.contactName}</div>
                                </div>
                                <div>
                                    <div style={s.label}>{lang['address']}</div>
                                    <div style={s.value}>{purchase.address}</div>
                                </div>
                                <div>
                                    <div style={s.label}>{lang['phone']}</div>
                                    <div style={s.value}>{purchase.contactPhone}</div>
                                </div>
                            </div>
                            <div style={s.grid2}>
                                <div>
                                    <div style={s.label}>{lang['deliveryMethod']}</div>
                                    <div style={s.value}>{carrierName || '\u2014'}</div>
                                </div>
                                <div>
                                    <div style={s.label}>{status === 'success' ? lang['estimatedDelivery'] : lang['deliveryDate']}</div>
                                    <div style={s.value}>{estimatedDate}</div>
                                </div>
                            </div>
                        </div>

                        {items.length > 0 && (
                            <div style={s.productsBox}>
                                {items.map((product: any, idx: number) => {
                                    const qty = (product.references?.length > 0 ? product.references[0].quantity : product.quantity) || 1;
                                    const referenceText = getReferenceDisplayText(product, homeState.default_language);
                                    return (
                                        <div key={idx} style={{
                                            ...s.productRow,
                                            borderBottom: idx === items.length - 1 ? 'none' : '1px solid #F5F5F5',
                                        }}>
                                            <div style={s.productName}>{product.productData.title}</div>
                                            <div style={s.productMeta}>{qty} {qty === 1 ? lang['piece'] : lang['pieces']}</div>
                                            {referenceText ? <div style={s.productMeta}>{referenceText}</div> : <div style={s.productMeta} />}
                                            <img style={s.productImg} src={getProductImage(product)} alt={product.productData.title} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                <div style={s.footer}>
                    <div style={s.footerTitle}>{lang['contactEcommerce'] ?? 'Contacta al comercio'}</div>
                    <div style={s.footerText}>
                        {contactEcommerce.phone} &nbsp;&bull;&nbsp; {contactEcommerce.email}
                    </div>
                </div>
            </div>
        );
    }
);

const PurchasePdfAveonline: FunctionComponent<Props> = (props) => {
    const printRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => props.onClose(),
    });

    useEffect(() => {
        const timer = setTimeout(() => { handlePrint(); }, 1500);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div style={{ display: 'none' }}>
            <PrintContent ref={printRef} {...props} />
        </div>
    );
};

export default PurchasePdfAveonline;
